import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { TopCallTimesService } from '../../modules/top-call-times/top-call-times.service';
import { Component, Input, OnInit } from '@angular/core';
import { CallMetricObj, TopCallTimesSettings, TopCallTimesTopOption } from '../../modules/top-call-times/top-call-times.model';

@Component({
	selector: 'cv-top-call-times-settings',
	templateUrl: './top-call-times-settings.component.html',
	styleUrls: ['./top-call-times-settings.component.scss']
})
export class TopCallTimesSettingsComponent implements OnInit {
	@Input() moduleManager: ModuleManager;

	public settings: TopCallTimesSettings = {
		top: 10,
		includes: [],
		selectedIncludes: [],
		sortList: [],
		selectedSort: null,
	};
	metricOptions = new Map<string, CallMetricObj>();
	selectedMetricIds: string[] = [];
	includes = null;
	isFirstOpen: boolean = true;
	dataLoading: boolean = false;
	topOptions: TopCallTimesTopOption[] = [
		{ Value: 10}, 
		{ Value: 25}, 
		{ Value: 50}, 
		{ Value: 100}, 
		{ Value: 250}, 
		{ Value: 500}, 
		{ Value: 1000}, 
		{ Value: 10000}
	];
	selectedTop: TopCallTimesTopOption;

	selectAll: boolean = false;

	_localizationStringMap: Map<string, string>;
    _localizationsLoaded: boolean = false;

	constructor(
		private cvLocalizationService: CVLocalizationService,
		private topCallTimesService: TopCallTimesService,
	) { }

	getLocalizationString(key, defaultValue): string {
		var value;
		if (this._localizationStringMap && typeof (value = this._localizationStringMap.get(key)) === "string")
			return value;
		return defaultValue;
	}

	async loadLocalizations(): Promise<void> {
		try{ 
			var localizationMap = await this.cvLocalizationService.GetSection("Top_Call_Times_Module");
			this._localizationStringMap = localizationMap;
		}
		catch{
			this._localizationStringMap = new Map();
			console.warn("Could not load localizations for section 'Top_Call_Times_Module'");
		}
		finally{
			this._localizationsLoaded = true;
		};
	}

	ngOnInit(): void {
		this.loadLocalizations();

		this.moduleManager.addSetting({
			name: 'topcalltimeSettings',
			getValue: ()  => { 
				return this.settings;
			},
			onSettingLoaded: (value) => {
				this.loadCustomSettings(value);
			}
		});
		this.moduleManager.registerEvents({
			onSettingsOpened: (isFirstOpen) => {
				if (this.isFirstOpen){
					this.moduleManager.loadSettings(false);
					this.isFirstOpen = false;
				}
			},
		});
		this.loadData();
		this.moduleManager.customSettingsAdded = true;
	}

	compareSelectList(o1: any, o2: any): boolean {
		return o1.Id === o2.Id;
	}

	async loadData(): Promise<void> {
		this.dataLoading = true;

		var response = await this.topCallTimesService.LoadCallMetrics(this.moduleManager.getModuleKey());
		if (response.status_code === 200) { //HTTP Success
			this.settings.includes = response.data.callMetricList;
			this.settings.sortList = response.data.metricSortList;

			if (this.settings.selectedSort == null) {
				this.settings.selectedSort = this.settings.sortList[0];
			}

			//Reset flags
			this.dataLoading = false;
		}
		else 
			this.dataLoading = false;
	}

	loadCustomSettings(value: any): void {
		if (typeof value === 'string') {
			if (value != "") {
				this.settings = JSON.parse(value);
			}
		} else if(value instanceof String) {
			this.settings = JSON.parse(value.toString());
		} 
		else if (value != undefined) {
			this.settings = value;
		}
		this.setupSettingsValues();
	}

	setupSettingsValues() {
		this.metricOptions.clear();
		this.selectedMetricIds = [];

		// for backwards compatibility -- the old widget saved this as a string
		let topValue = 10;
		if (typeof this.settings.top === "string")
			topValue = parseInt(this.settings.top);
		else topValue = this.settings.top;

		this.settings.includes.forEach(metric => this.metricOptions.set(metric?.Id, metric));
		this.settings.selectedIncludes.forEach(metric => this.selectedMetricIds.push(metric?.Id));
		this.selectedTop = this.topOptions.find(option => option.Value === topValue);
	}

	getMetricDisplay(): string {
		let maxString = 28; // max length to fit in the select display
		if (this.selectedMetricIds.length === this.settings?.includes?.length)
			return "All";
		else if(this.selectedMetricIds.length > 0) {
		if (this.metricOptions.size === 0)
			this.settings.includes.forEach(metric => this.metricOptions.set(metric?.Id, metric));
		let displayText = this.metricOptions.get(this.selectedMetricIds[0])?.CallMetricName;
			for(let i = 1; i < this.selectedMetricIds.length; i++) {
				let nextMetricName = this.metricOptions.get(this.selectedMetricIds[i])?.CallMetricName;
			    if(displayText && nextMetricName && displayText.length + nextMetricName.length < maxString) {
			        displayText += ", " + nextMetricName;
			    }
				else {
					displayText += `, ... (${this.selectedMetricIds.length - i})`;
					break;
				}
			}
			return displayText;
		}
		else return "";
	}

	getSlideoutContextSectionText(): string {
		if (!this._localizationsLoaded)
			return "";

		var topCallCount = this.getLocalizationString("Slideout_Context_Top_Calls", "Top CALL_LIMIT");
		topCallCount = topCallCount.replace("CALL_LIMIT", this.settings.top.toString());

		var sortedBy = this.getLocalizationString("Slideout_Context_Sorted_By", "Sorted by METRIC_NAME");
		var metricName = "-";
		if (this.settings.selectedSort && this.settings.selectedSort.Value)
			metricName = this.settings.selectedSort.Value;
		sortedBy = sortedBy.replace("METRIC_NAME", metricName);

		// Count first, because the string depends on the count
		var metricCount = this.settings.selectedIncludes.length;
		var metricsIncluded;
		switch (metricCount) {
			case 0:
				metricsIncluded = this.getLocalizationString("Slideout_Context_No_Metrics", "No metrics included");
				break;
			case 1:
				metricsIncluded = this.getLocalizationString("Slideout_Context_Single_Metric", "1 metric included");
				break;
			default:
				metricsIncluded = this.getLocalizationString("Slideout_Context_Multiple_Metrics", "METRIC_COUNT metrics included");
				metricsIncluded = metricsIncluded.replace("METRIC_COUNT", metricCount);
				break;
		}

		return topCallCount + " | " + sortedBy + " | " + metricsIncluded;
	};

	onSelectedTopChanged(event) {
		this.selectedTop = this.topOptions.find(option => option.Value === event);
		this.settings.top = event;
	}

	onSelectedSortChanged(event) {
		this.settings.selectedSort = this.settings.sortList.find(sort => sort.Id === event);
	}

	onSelectedMetricsChanged(event) {
		this.selectedMetricIds = event.value;
		this.settings.selectedIncludes = [];
		this.selectedMetricIds.forEach(id => {
			this.settings.selectedIncludes.push(this.metricOptions.get(id));
		})
	}
}
